/*
 * Sticky product bar behaviour.
 *
 * Extracted from sections/sticky-product-bar.liquid. It was a plain inline
 * <script> in the middle of the body, so its 26 KB had to be parsed and
 * compiled synchronously before the parser could continue. Loaded with
 * defer it is fetched in parallel and runs after the document is parsed.
 *
 * The two values Liquid used to interpolate (section id, loading label) are
 * read from data attributes on the bar instead.
 */
(() => {
  /*
   * The bar used to be looked up by an id Liquid interpolated into this script.
   * Running from a static asset there is no section id to interpolate, so it is
   * found by its data attribute instead.
   *
   * The lookup stays inside the function rather than being hoisted to a
   * module-level const: shopify:section:load fires when a section is added or
   * re-rendered in the theme editor, and a one-time lookup at script execution
   * would have captured null for a section that did not exist on first load.
   */
  function initialiseStickyProductBar() {
    const bar = document.querySelector("[data-sticky-product-bar]");

    if (!bar || bar.dataset.initialised === "true") {
      return;
    }

    bar.dataset.initialised = "true";

    const purchaseSelect = bar.querySelector(
      "[data-sticky-purchase-option]"
    );

    const selectorWrapper = bar.querySelector(
      ".sticky-product-bar__selector"
    );

    const selectorToggle = bar.querySelector(
      "[data-sticky-selector-toggle]"
    );

    const selectorMenu = bar.querySelector(
      "[data-sticky-options-menu]"
    );

    const selectedLabel = bar.querySelector(
      "[data-sticky-selected-label]"
    );

    const selectedSave = bar.querySelector(
      "[data-sticky-selected-save]"
    );

    const selectedMeta = bar.querySelector(
      "[data-sticky-selected-meta]"
    );

    const customOptionButtons = Array.from(
      bar.querySelectorAll("[data-sticky-option-value]")
    );

    const stickyAddButton = bar.querySelector(
      "[data-sticky-add-to-cart]"
    );

    const errorElement = bar.querySelector(
      "[data-sticky-product-error]"
    );

    const usesCurrentProduct =
      bar.dataset.usesCurrentProduct === "true";

    let subscriptionWidget = null;
    let mainProductForm = null;
    let mainAddButton = null;
    let selectionObserver = null;
    let mainSelectionRequestId = 0;
    let synchronizingMainSelection = false;

    const stickyOptions = Array.from(
      purchaseSelect?.options || []
    );

    const stickyVariantIds = new Set(
      stickyOptions
        .map((option) => String(option.dataset.variantId || ""))
        .filter(Boolean)
    );

    function showError(message) {
      if (!errorElement) return;

      errorElement.textContent = message || "";
      errorElement.hidden = !message;
    }

    function getSelectedOption() {
      if (!purchaseSelect) return null;

      return purchaseSelect.options[
        purchaseSelect.selectedIndex
      ];
    }

    function getOptionByTerm(term) {
      return stickyOptions.find(
        (option) => String(option.dataset.term || "") === String(term)
      );
    }

    function getOptionByVariantId(variantId) {
      return stickyOptions.find(
        (option) =>
          String(option.dataset.variantId || "") ===
          String(variantId || "")
      );
    }

    function syncCustomSelector() {
      const selectedOption = getSelectedOption();
      if (!selectedOption) return;

      const label =
        selectedOption.dataset.label ||
        selectedOption.textContent.trim();

      const saveLabel =
        selectedOption.dataset.saveLabel || "";

      const meta =
        selectedOption.dataset.meta || "";

      if (selectedLabel) {
        selectedLabel.textContent = label;
      }

      if (selectedSave) {
        selectedSave.textContent = saveLabel;
        selectedSave.hidden = !saveLabel;
      }

      if (selectedMeta) {
        selectedMeta.textContent = meta;
      }

      customOptionButtons.forEach((button) => {
        const isSelected =
          button.dataset.stickyOptionValue ===
          selectedOption.value;

        button.setAttribute(
          "aria-selected",
          isSelected ? "true" : "false"
        );
      });
    }

    function setSelectorOpen(open) {
      if (!selectorMenu || !selectorToggle) return;

      selectorMenu.hidden = !open;
      selectorToggle.setAttribute(
        "aria-expanded",
        open ? "true" : "false"
      );

      selectorWrapper?.classList.toggle(
        "is-open",
        open
      );
    }

    function findMatchingSubscriptionControllers() {
      const controllers = new Set(
        Array.from(document.querySelectorAll(
          "sub-widget, subscription-popup, subscription-disclosure"
        )).map((element) =>
          element.closest("subscription-popup, subscription-disclosure") || element
        )
      );
      return Array.from(controllers).filter((controller) => {
          const widget = controller.matches("sub-widget")
            ? controller
            : controller.querySelector("sub-widget") ||
              controller.querySelector("template")?.content.querySelector("sub-widget");
          const plans = Array.from(
            widget?.querySelectorAll(
              ".wt-subscription-widget__plans[data-variant-id]"
            ) || []
          );

          return plans.some((plan) =>
            stickyVariantIds.has(
              String(plan.dataset.variantId || "")
            )
          );
      });
    }

    function findMatchingSubscriptionWidget() {
      const controller = findMatchingSubscriptionControllers()[0];
      return controller?.querySelector("sub-widget") || controller || null;
    }

    function findProductFormForWidget(widget) {
      if (!widget) return null;

      const widgetWrapper = widget.closest(
        ".wt-subscription-widget[data-section-id]"
      );

      const productSectionId =
        widgetWrapper?.dataset?.sectionId || "";

      if (productSectionId) {
        const formById = document.getElementById(
          `product-form-${productSectionId}`
        );

        if (formById) return formById;

        const shopifySection = document.getElementById(
          `shopify-section-${productSectionId}`
        );

        const formInShopifySection =
          shopifySection?.querySelector(
            'product-form form[action*="/cart/add"], form[id^="product-form-"][action*="/cart/add"], form[action*="/cart/add"]'
          );

        if (formInShopifySection) {
          return formInShopifySection;
        }

        const productSection = Array.from(
          document.querySelectorAll("[data-section-id]")
        ).find(
          (element) =>
            element.dataset.sectionId === productSectionId &&
            element.querySelector('form[action*="/cart/add"]')
        );

        if (productSection) {
          return productSection.querySelector(
            'product-form form[action*="/cart/add"], form[id^="product-form-"][action*="/cart/add"], form[action*="/cart/add"]'
          );
        }
      }

      return null;
    }

    function refreshAssociations() {
      subscriptionWidget = findMatchingSubscriptionWidget();

      if (usesCurrentProduct) {
        mainProductForm =
          findProductFormForWidget(subscriptionWidget) ||
          document.querySelector(
            'product-form form[action*="/cart/add"], form[id^="product-form-"][action*="/cart/add"], form[action*="/cart/add"]'
          );

        mainAddButton =
          mainProductForm?.querySelector(
            '[type="submit"][name="add"]'
          ) ||
          mainProductForm?.querySelector(
            'button[type="submit"]'
          ) ||
          null;
      } else {
        mainProductForm = null;
        mainAddButton = null;
      }
    }

    function syncStickyFromMainWidget() {
      if (!subscriptionWidget || synchronizingMainSelection) return;

      const selectedPlan = subscriptionWidget.querySelector(
        ".wt-subscription-widget__plans--selected[data-term]"
      );

      if (!selectedPlan) return;

      const matchingOption =
        getOptionByTerm(selectedPlan.dataset.term) ||
        getOptionByVariantId(selectedPlan.dataset.variantId);

      if (matchingOption) {
        purchaseSelect.value = matchingOption.value;
        syncCustomSelector();
        showError("");
      }
    }

    async function synchronizeMainProductWidget(term) {
      const requestId = ++mainSelectionRequestId;
      synchronizingMainSelection = true;
      try {
        const widgets = await Promise.all(
          findMatchingSubscriptionControllers().map(async (controller) => {
            if (controller.matches("subscription-popup")) {
              return controller.prepareWidget();
            }
            if (controller.matches("subscription-disclosure")) {
              await controller.loadWidgetScript();
              return controller.querySelector("sub-widget");
            }
            return controller;
          })
        );
        if (requestId !== mainSelectionRequestId || !bar.isConnected) return;

        if (widgets.some((widget) => typeof widget?.handlePlanClick !== "function")) {
          throw new Error("The purchase-options script did not load.");
        }

        const option = getOptionByTerm(term);
        widgets.forEach((widget) => {
          if (!widget || typeof widget.handlePlanClick !== "function") return;
          const mainPlan = Array.from(widget.querySelectorAll(
            ".wt-subscription-widget__plans[data-variant-id]"
          )).find((plan) =>
            String(plan.dataset.term || "") === String(term) &&
            String(plan.dataset.variantId || "") === String(option?.dataset.variantId || "") &&
            String(plan.dataset.sellingPlanId || "") === String(option?.dataset.sellingPlanId || "")
          );
          if (mainPlan) widget.handlePlanClick(mainPlan, true, true);
        });
        refreshAssociations();
        bindMainWidgetObserver();
      } catch (error) {
        if (requestId === mainSelectionRequestId) {
          showError("The purchase option could not be updated. Please try again.");
        }
        console.error("Sticky product selection error:", error);
      } finally {
        if (requestId === mainSelectionRequestId) {
          synchronizingMainSelection = false;
        }
      }
    }

    function bindMainWidgetObserver() {
      selectionObserver?.disconnect();
      selectionObserver = null;

      if (!subscriptionWidget) return;

      selectionObserver = new MutationObserver(
        syncStickyFromMainWidget
      );

      selectionObserver.observe(subscriptionWidget, {
        subtree: true,
        attributes: true,
        attributeFilter: ["class"]
      });
    }

    selectorToggle?.addEventListener("click", () => {
      const isOpen =
        selectorToggle.getAttribute("aria-expanded") ===
        "true";

      setSelectorOpen(!isOpen);
    });

    customOptionButtons.forEach((button) => {
      button.addEventListener("click", () => {
        if (button.disabled || !purchaseSelect) return;

        purchaseSelect.value =
          button.dataset.stickyOptionValue || "";

        purchaseSelect.dispatchEvent(
          new Event("change", {
            bubbles: true
          })
        );

        setSelectorOpen(false);
        selectorToggle?.focus();
      });
    });

    document.addEventListener("click", (event) => {
      if (
        selectorWrapper &&
        !selectorWrapper.contains(event.target)
      ) {
        setSelectorOpen(false);
      }
    });

    document.addEventListener("keydown", (event) => {
      if (event.key === "Escape") {
        setSelectorOpen(false);
        selectorToggle?.focus();
      }
    });

    syncCustomSelector();
    refreshAssociations();
    bindMainWidgetObserver();
    syncStickyFromMainWidget();

    window.setTimeout(() => {
      refreshAssociations();
      bindMainWidgetObserver();
      syncStickyFromMainWidget();
      updateStickyBarVisibility();
    }, 250);

    purchaseSelect?.addEventListener("change", () => {
      showError("");
      syncCustomSelector();

      const selectedOption = getSelectedOption();
      if (!selectedOption) return;

      synchronizeMainProductWidget(
        selectedOption.dataset.term || ""
      );
    });

    document.addEventListener(
      "subscription:change",
      (event) => {
        const detail = event.detail || {};
        if (synchronizingMainSelection ||
            !stickyVariantIds.has(String(detail.variantId || ""))) return;

        subscriptionWidget = event.target;
        bindMainWidgetObserver();

        const matchingOption =
          getOptionByTerm(detail.term) ||
          getOptionByVariantId(detail.variantId);

        if (matchingOption) {
          purchaseSelect.value = matchingOption.value;
          syncCustomSelector();
          showError("");
        }
      }
    );

    function getAssociatedFormControl(form, name) {
      if (!form || !name) return null;

      const controlInsideForm = form.querySelector(
        `[name="${name}"]`
      );

      if (controlInsideForm) {
        return controlInsideForm;
      }

      /*
       * The subscription widget's selling_plan input can sit outside
       * the product form and connect to it through the form attribute.
       * Checking the native .form property finds both internal and
       * externally associated controls without depending on IDs.
       */
      return (
        Array.from(
          document.querySelectorAll(`[name="${name}"]`)
        ).find((control) => control.form === form) || null
      );
    }

    function syncMainProductFormFields(
      form,
      variantId,
      sellingPlanId
    ) {
      if (!form) return;

      const variantInput = getAssociatedFormControl(
        form,
        "id"
      );

      if (variantInput && variantId) {
        const value = String(variantId);
        variantInput.value = value;
        variantInput.setAttribute("value", value);
      }

      let sellingPlanInput = getAssociatedFormControl(
        form,
        "selling_plan"
      );

      /*
       * A selling-plan input normally comes from subscription-widget,
       * but create one as a defensive fallback if the sticky bar is
       * used with a product form that does not render that input.
       */
      if (!sellingPlanInput && sellingPlanId) {
        sellingPlanInput = document.createElement("input");
        sellingPlanInput.type = "hidden";
        sellingPlanInput.name = "selling_plan";
        form.appendChild(sellingPlanInput);
      }

      if (sellingPlanInput) {
        if (sellingPlanId) {
          const value = String(sellingPlanId);
          sellingPlanInput.value = value;
          sellingPlanInput.setAttribute("value", value);
        } else {
          sellingPlanInput.value = "";
          sellingPlanInput.removeAttribute("value");
        }
      }
    }

    function submitMainProductForm(form, submitButton) {
      if (!form) return false;

      if (typeof form.requestSubmit === "function") {
        if (
          submitButton &&
          submitButton.form === form
        ) {
          form.requestSubmit(submitButton);
        } else {
          form.requestSubmit();
        }

        return true;
      }

      if (submitButton) {
        submitButton.click();
        return true;
      }

      return form.dispatchEvent(
        new Event("submit", {
          bubbles: true,
          cancelable: true
        })
      );
    }

    function restoreStickyButton(
      originalButtonText,
      delay = 0
    ) {
      window.setTimeout(() => {
        if (!stickyAddButton?.isConnected) return;

        stickyAddButton.disabled = false;
        stickyAddButton.textContent =
          originalButtonText;
      }, delay);
    }

    async function addStickyProductToCart() {
      showError("");

      const selectedOption = getSelectedOption();

      if (!selectedOption) {
        showError("Please select a purchase option.");
        return;
      }

      if (
        selectedOption.disabled ||
        selectedOption.dataset.available === "false"
      ) {
        showError(
          "This purchase option is currently unavailable."
        );
        return;
      }

      const variantId =
        selectedOption.dataset.variantId || "";

      const sellingPlanId =
        selectedOption.dataset.sellingPlanId || "";

      if (!variantId) {
        showError(
          "The selected product variant is unavailable."
        );
        return;
      }

      /*
       * Keep the main subscription widget and its product form aligned
       * with the term selected in the sticky bar.
       */
      const originalButtonText =
        stickyAddButton.textContent;

      stickyAddButton.disabled = true;
      stickyAddButton.textContent =
        bar.dataset.loadingLabel || "Adding\u2026";

      await synchronizeMainProductWidget(
        selectedOption.dataset.term || ""
      );
      refreshAssociations();

      /*
       * On product pages, submit the theme's real Shopify product form.
       * This preserves the theme's native cart-drawer handling,
       * validation, quantity, line-item properties and any custom
       * recipient fields. It also avoids manually passing an incomplete
       * section response into cart-drawer's renderContents method.
       */
      if (usesCurrentProduct && mainProductForm) {
        try {
          syncMainProductFormFields(
            mainProductForm,
            variantId,
            sellingPlanId
          );

          const submitted = submitMainProductForm(
            mainProductForm,
            mainAddButton
          );

          if (!submitted) {
            throw new Error(
              "The product form could not be submitted."
            );
          }

          /*
           * product-form.js completes asynchronously. Restore only the
           * sticky button; the theme continues controlling the actual
           * product-form button, validation and cart drawer.
           */
          restoreStickyButton(
            originalButtonText,
            1400
          );
        } catch (error) {
          console.error(
            "Sticky product form submission error:",
            error
          );

          showError(
            "The product could not be added. Please try again."
          );

          restoreStickyButton(originalButtonText);
        }

        return;
      }

      /*
       * Non-product pages may not contain a Shopify product form.
       * Retain the existing Ajax add flow there, but protect the cart
       * rendering step. If the installed theme's drawer markup does not
       * match renderContents(), send the customer to the cart rather
       * than exposing a DOMParser/null.innerHTML error.
       */
      let itemWasAdded = false;

      try {
        const cartComponent =
          document.querySelector("cart-drawer") ||
          document.querySelector("cart-notification");

        const item = {
          id: Number(variantId),
          quantity: 1
        };

        if (sellingPlanId) {
          item.selling_plan =
            Number(sellingPlanId);
        }

        const requestBody = {
          items: [item]
        };

        if (
          cartComponent &&
          typeof cartComponent
            .getSectionsToRender === "function"
        ) {
          const sections = cartComponent
            .getSectionsToRender()
            .map((section) => section?.section)
            .filter(Boolean);

          if (sections.length > 0) {
            requestBody.sections = sections;
            requestBody.sections_url =
              window.location.pathname;
          }
        }

        const root =
          window.Shopify?.routes?.root || "/";

        const response = await fetch(
          `${root}cart/add.js`,
          {
            method: "POST",
            headers: {
              "Content-Type":
                "application/json",
              Accept: "application/json"
            },
            body: JSON.stringify(
              requestBody
            )
          }
        );

        const responseData =
          await response.json();

        if (!response.ok) {
          throw new Error(
            responseData.description ||
              responseData.message ||
              "The product could not be added."
          );
        }

        itemWasAdded = true;

        if (
          cartComponent &&
          typeof cartComponent
            .renderContents === "function"
        ) {
          try {
            cartComponent.renderContents(
              responseData
            );

            if (
              typeof cartComponent.open ===
              "function"
            ) {
              window.setTimeout(() => {
                cartComponent.open();
              }, 50);
            }
          } catch (renderError) {
            console.error(
              "Sticky cart rendering error:",
              renderError
            );

            window.location.assign(
              `${root}cart`
            );
            return;
          }
        } else {
          window.location.assign(`${root}cart`);
          return;
        }
      } catch (error) {
        console.error(
          "Sticky product bar error:",
          error
        );

        /*
         * The cart request can succeed before a drawer-rendering error.
         * Never invite a second click in that case; open the cart where
         * the successfully added line item can be confirmed.
         */
        if (itemWasAdded) {
          const root =
            window.Shopify?.routes?.root || "/";

          window.location.assign(`${root}cart`);
          return;
        }

        showError(
          "The product could not be added. Please try again."
        );
      } finally {
        restoreStickyButton(originalButtonText);
      }
    }

    stickyAddButton?.addEventListener(
      "click",
      addStickyProductToCart
    );

    const alwaysVisible =
      bar.dataset.alwaysVisible === "true";

    function applyPageSpacing() {
      const barIsVisible =
        bar.classList.contains("is-visible");

      if (
        window.innerWidth >= 750 ||
        !barIsVisible ||
        window.getComputedStyle(bar).display === "none"
      ) {
        document.documentElement.style.removeProperty(
          "--sticky-product-bar-height"
        );
        return;
      }

      document.documentElement.style.setProperty(
        "--sticky-product-bar-height",
        `${bar.offsetHeight}px`
      );
    }

    function setBarVisibility(show) {
      bar.classList.toggle("is-visible", show);
      window.requestAnimationFrame(applyPageSpacing);
    }

    function updateStickyBarVisibility() {
      if (window.innerWidth >= 750) {
        setBarVisibility(false);
        return;
      }

      if (alwaysVisible || !usesCurrentProduct) {
        setBarVisibility(true);
        return;
      }

      if (!mainAddButton || !mainAddButton.isConnected) {
        refreshAssociations();
      }

      if (!mainAddButton) {
        setBarVisibility(true);
        return;
      }

      const buttonPosition =
        mainAddButton.getBoundingClientRect();

      setBarVisibility(buttonPosition.bottom < 0);
    }

    updateStickyBarVisibility();

    window.addEventListener(
      "scroll",
      updateStickyBarVisibility,
      { passive: true }
    );

    window.addEventListener(
      "resize",
      updateStickyBarVisibility
    );

    window.addEventListener("resize", applyPageSpacing);

    if (typeof ResizeObserver !== "undefined") {
      const barResizeObserver = new ResizeObserver(
        applyPageSpacing
      );

      barResizeObserver.observe(bar);
    }
  }

  if (document.readyState === "loading") {
    document.addEventListener(
      "DOMContentLoaded",
      initialiseStickyProductBar
    );
  } else {
    initialiseStickyProductBar();
  }

  document.addEventListener(
    "shopify:section:load",
    initialiseStickyProductBar
  );
})();
